import { useEffect, useState } from 'react'
import { Keyboard, Platform, ScrollView, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Pressable } from '@/components/ui/Pressable'
import { TextInput } from '@/components/ui/TextInput'
import { TrackedNativePressable as RNPressable } from '@/components/ui/TrackedNativePressable'
import { ShieldCheck, X } from '@/components/ui/icons'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, INTERACTION } from '@/lib/theme/tokens'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'

export interface GuardianSetupValues {
  guardianName: string
  responsibleAdultName: string
  emergencyContactName: string
  emergencyContactPhone: string
  includesMinors: boolean
  consentAccepted: true
}

interface GuardianSetupModalProps {
  visible: boolean
  loading: boolean
  onClose: () => void
  onSubmit: (values: GuardianSetupValues) => void
}

export function GuardianSetupModal({
  visible,
  loading,
  onClose,
  onSubmit,
}: GuardianSetupModalProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const [guardianName, setGuardianName] = useState('')
  const [responsibleAdultName, setResponsibleAdultName] = useState('')
  const [emergencyContactName, setEmergencyContactName] = useState('')
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('')
  const [includesMinors, setIncludesMinors] = useState(false)
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!visible) setSubmitted(false)
  }, [visible])

  const complete =
    guardianName.trim().length > 0 &&
    responsibleAdultName.trim().length > 0 &&
    emergencyContactName.trim().length > 0 &&
    emergencyContactPhone.trim().length > 0 &&
    consentAccepted

  const submit = () => {
    setSubmitted(true)
    if (!complete) return
    Keyboard.dismiss()
    onSubmit({
      guardianName: guardianName.trim(),
      responsibleAdultName: responsibleAdultName.trim(),
      emergencyContactName: emergencyContactName.trim(),
      emergencyContactPhone: emergencyContactPhone.trim(),
      includesMinors,
      consentAccepted: true,
    })
  }

  return (
    <AppModal
      visible={visible}
      transparent
      animationType="slide"
      presentationStyle="overFullScreen"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <RNPressable
          style={{ flex: 1 }}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
        />
        <GlassSheet
          accessibilityLabel={t('tours.guardian.setup_title')}
          style={{
            maxHeight: '88%',
            width: '100%',
            maxWidth: Platform.OS === 'web' ? 560 : undefined,
            alignSelf: 'center',
            paddingTop: SPACING.md,
            paddingBottom: Math.max(insets.bottom, SPACING.lg),
            borderTopLeftRadius: RADIUS.xl,
            borderTopRightRadius: RADIUS.xl,
          }}
          testID="tours.guardian-sheet"
        >
          <View
            style={{
              width: 36,
              height: 4,
              alignSelf: 'center',
              borderRadius: 2,
              backgroundColor: colors.border,
            }}
          />
          <View
            style={{
              minHeight: INTERACTION.minTouchTarget,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              paddingHorizontal: SPACING.lg,
              marginVertical: SPACING.sm,
            }}
          >
            <View
              style={{
                width: 38,
                height: 38,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 19,
                backgroundColor: colors.surfaceSubtle,
              }}
            >
              <ShieldCheck size={21} color={colors.primary} />
            </View>
            <Text
              style={{
                flex: 1,
                fontFamily: FONTS.bodyBold,
                fontSize: 19,
                color: colors.textPrimary,
              }}
            >
              {t('tours.guardian.setup_title')}
            </Text>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              hitSlop={12}
              style={{
                width: INTERACTION.minTouchTarget,
                height: INTERACTION.minTouchTarget,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={22} color={colors.textSecondary} />
            </Pressable>
          </View>
          <ScrollView
            style={{ flexShrink: 1 }}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            contentContainerStyle={{ paddingHorizontal: SPACING.lg, paddingBottom: SPACING.sm }}
          >
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 20,
                color: colors.textSecondary,
                marginBottom: SPACING.lg,
              }}
            >
              {t('tours.guardian.setup_body')}
            </Text>
            <TextInput
              label={t('tours.guardian.guardian_name')}
              value={guardianName}
              onChangeText={setGuardianName}
              autoCapitalize="words"
              error={submitted && !guardianName.trim() ? t('common.required') : undefined}
            />
            <TextInput
              label={t('tours.guardian.responsible_adult')}
              value={responsibleAdultName}
              onChangeText={setResponsibleAdultName}
              autoCapitalize="words"
              error={submitted && !responsibleAdultName.trim() ? t('common.required') : undefined}
            />
            <TextInput
              label={t('tours.guardian.emergency_contact')}
              value={emergencyContactName}
              onChangeText={setEmergencyContactName}
              autoCapitalize="words"
              error={submitted && !emergencyContactName.trim() ? t('common.required') : undefined}
            />
            <TextInput
              label={t('tours.guardian.emergency_phone')}
              value={emergencyContactPhone}
              onChangeText={setEmergencyContactPhone}
              keyboardType="phone-pad"
              error={submitted && !emergencyContactPhone.trim() ? t('common.required') : undefined}
            />
            <Checkbox
              checked={includesMinors}
              onChange={setIncludesMinors}
              label={t('tours.guardian.includes_minors')}
              testID="tours.guardian-minors"
            />
            <Checkbox
              checked={consentAccepted}
              onChange={setConsentAccepted}
              label={t('tours.guardian.consent')}
              required
              error={
                submitted && !consentAccepted ? t('tours.guardian.consent_required') : undefined
              }
              testID="tours.guardian-consent"
            />
            <Button
              title={t('tours.guardian.create_link')}
              size="lg"
              loading={loading}
              onPress={submit}
              style={{ marginTop: SPACING.sm }}
              testID="tours.guardian-create"
            />
          </ScrollView>
        </GlassSheet>
      </View>
    </AppModal>
  )
}
